/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IButtonProps, IButtonStyles, IconButton } from '@fluentui/react/lib/Button';
import { IStackProps, Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisTooltip } from 'src/core/components/LuisTooltip';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getStylistV2 } from 'src/core/themes/luisStylist';

export type PatternCellOptionsProps = {
    /**
     * Indicates if the menu items is hidden or not.
     */
    isHidden: boolean;

    /**
     * Callback to set the edit mode.
     */
    onClickEdit?: () => void;

    /**
     * Callback to notify that delete action is required.
     */
    onClickDelete?: () => void;
};

const messages = defineMessages({
    edit: {
        id: 'patterns.patternCellOptions.edit',
        defaultMessage: 'Edit'
    },
    delete: {
        id: 'patterns.patternCellOptions.delete',
        defaultMessage: 'Delete'
    }
});

const { styleComponent, styleDiv } = getStylistV2('PatternCellOptions');

const OptionsContainer = styleComponent<IStackProps>(Stack)(
    'OptionsContainer',
    (_, cssVars) => ({
        backgroundColor: 'transparent',
        opacity: cssVars.opacity,
        position: 'absolute',
        right: '0px'
    }),
    {
        cssVars: { opacity: '' },
        cssVarsSetter: ({ isHidden }: { isHidden: boolean }) => ({
            opacity: isHidden ? 0 : 1
        })
    }
);

const Layer = styleDiv('Layer', {
    backgroundColor: 'white'
});

const OverlayDiv = styleDiv('OverlayDiv', {
    height: '24px',
    width: '150px',
    backgroundImage: 'linear-gradient(to right, transparent 20%, white 100% )'
});

const iconButtonStyles = classNamesFunction<IButtonProps, IButtonStyles>()({
    rootDisabled: { backgroundColor: 'white' }
});

/**
 * The contextual menu that appears in the pattern row (on hovering) and manages the
 * edit and delete actions for the specified pattern.
 *
 * @param props The properties of the contextual menu such as the actions that
 * should be taken when an action is issued.
 */
export const PatternCellOptions = (props: PatternCellOptionsProps) => {
    const { isHidden, onClickEdit: onEditClicked, onClickDelete: onDeleteClicked } = props;
    const { localize } = useLocalization();

    return (
        <OptionsContainer horizontal customProps={{ isHidden }} verticalAlign="start">
            <OverlayDiv />
            <Layer>
                <LuisTooltip content={localize(messages.edit)} hidden={isHidden}>
                    <IconButton
                        data-automation-id="editUtteranceButton"
                        styles={iconButtonStyles}
                        iconProps={{ iconName: 'Edit' }}
                        onClick={onEditClicked}
                        disabled={isHidden}
                    />
                </LuisTooltip>

                <LuisTooltip content={localize(messages.delete)} hidden={isHidden}>
                    <IconButton
                        data-automation-id="deleteUtteranceButton"
                        styles={iconButtonStyles}
                        iconProps={{ iconName: 'Delete' }}
                        onClick={onDeleteClicked}
                        disabled={isHidden}
                    />
                </LuisTooltip>
            </Layer>
        </OptionsContainer>
    );
};
